// src/lib/query-client.ts
//
// Cache Strategy:
// - Default staleTime: 2 minutes (query-client default)
// - Essential data (clients, jobs, invoices): 5 minutes (useReactQueryData)
// - Background data (services, categories): 10 minutes (useReactQueryData)
// - Background queries disable refetchOnMount/Focus to reduce API calls

import { QueryClient, Query } from '@tanstack/react-query'

export const CACHE_TIMES = {
  DEFAULT_STALE: 2 * 60 * 1000,      // 2 minutes
  ESSENTIAL_STALE: 5 * 60 * 1000,    // 5 minutes
  BACKGROUND_STALE: 10 * 60 * 1000,  // 10 minutes
  GC_TIME: 10 * 60 * 1000,           // 10 minutes
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Smart retry with 406 error handling
      retry: (failureCount, error: any) => {
        // Don't retry 406 errors (RLS/permission issues) or 403 errors
        if (error?.status === 406 || error?.status === 403) {
          console.warn(`[QueryClient] Skipping retry due to ${error.status} error:`, error.message)
          return false
        }
        // Don't retry more than 3 times
        return failureCount < 3
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Disable automatic interval refetching to prevent excessive API calls
      refetchInterval: false,

      // Keep data fresh for 2 minutes - shorter staleTime means fresher data
      staleTime: CACHE_TIMES.DEFAULT_STALE,

      // Keep unused data in cache for 10 minutes
      gcTime: CACHE_TIMES.GC_TIME,

      // Enable smart refetching for better UX
      // Only refetch if data is stale (older than staleTime)
      refetchOnWindowFocus: (query: Query) => {
        // Only refetch if the query is stale
        const isStale = query.state.dataUpdatedAt 
          ? Date.now() - query.state.dataUpdatedAt > (query.staleTime || CACHE_TIMES.DEFAULT_STALE)
          : true
        
        return isStale
      },
      refetchOnReconnect: true,    // Enable - refetch when internet reconnects
      refetchOnMount: true,        // CRITICAL FIX: Enable to fetch fresh data when components mount
      
      // Enhanced error handling
      onError: (error: any) => {
        // Don't spam console with 406 errors
        if (error?.status !== 406) {
          console.error('[QueryClient] Query error:', error)
        }
      },
    },
    mutations: {
      // Retry mutations once (with same 406 handling)
      retry: (failureCount, error: any) => {
        if (error?.status === 406 || error?.status === 403) {
          console.warn(`[QueryClient] Skipping mutation retry due to ${error.status} error:`, error.message)
          return false
        }
        return failureCount < 1
      },
      retryDelay: 1000,
    },
  },
})

// Query key factory for consistent key generation
export const queryKeys = {
  // Base keys
  companies: ['companies'] as const,
  company: (id: string) => ['companies', id] as const,
  
  // Entity keys with company context
  entity: (entityName: string, companyId: string) => [entityName, companyId] as const,
  
  // Role-based keys
  roleData: (role: string, companyId: string) => ['roleData', role, companyId] as const,
  
  // Time-based keys
  timeData: (timeOfDay: string, companyId: string) => ['timeData', timeOfDay, companyId] as const,
  
  // All company data
  allCompanyData: (companyId: string) => ['allCompanyData', companyId] as const,
}

// Helper to get query key for any entity
export const getEntityQueryKey = (entityName: string, companyId: string) => {
  return queryKeys.entity(entityName, companyId)
}